// client/src/coms/settings/useHotkeySettings.js
// Mute hotkeys (every platform) and push-to-talk (desktop app only, where the app registers global hotkeys).
// Hotkey settings via the desktop bridge. One capture is active at a time.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setPushToTalkEnabled, setPushToTalkKeyCode, emitPushToTalkPrefsChanged } from '../voip/pushToTalkPrefs';
import { MUTE_HOTKEYS, announceMuteHotkeys, readAllMuteHotkeys, shortcutFromKeyEvent, writeMuteHotkey, describeShortcutProblem } from './muteHotkeys';

export function isTauri() {
  try {
    return !!(window.__TAURI__ && window.__TAURI__.core && typeof window.__TAURI__.core.invoke === 'function');
  } catch {
    return false;
  }
}

async function invokeTauri(command, payload) {
  try {
    if (!isTauri()) return null;
    return await window.__TAURI__.core.invoke(command, payload);
  } catch {
    return null;
  }
}

async function pushMuteHotkeysToDesktop(all) {
  if (!isTauri()) return;
  await invokeTauri('set_mute_mic_shortcut', { shortcut: all.mic || '' });
  await invokeTauri('set_mute_speaker_shortcut', { shortcut: all.speaker || '' });
  await invokeTauri('set_mute_both_shortcut', { shortcut: all.both || '' });
}

export const DEFAULT_PTT_SHORTCUT = 'F9';

export default function useHotkeySettings() {
  const inTauri = useMemo(() => isTauri(), []);
  const [hotkeys, setHotkeys] = useState(() => readAllMuteHotkeys());
  const [pttEnabled, setPttEnabled] = useState(false);
  const [pttShortcut, setPttShortcut] = useState(DEFAULT_PTT_SHORTCUT);
  const [capturing, setCapturing] = useState(''); // 'ptt' | 'mic' | 'speaker' | 'both' | ''
  const [problem, setProblem] = useState({ kind: '', text: '' });
  const hotkeysRef = useRef(hotkeys);
  hotkeysRef.current = hotkeys;

  const applyHotkey = useCallback(async (kind, value) => {
    writeMuteHotkey(kind, value);
    const all = readAllMuteHotkeys();
    setHotkeys(all);
    announceMuteHotkeys();
    await pushMuteHotkeysToDesktop(all);
  }, []);

  // Desktop app: load push-to-talk and the mute hotkeys the app has registered.
  useEffect(() => {
    if (!inTauri) {
      setPushToTalkEnabled(false);
      setPushToTalkKeyCode('Space');
      emitPushToTalkPrefsChanged({ enabled: false, keyCode: 'Space' });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const s = await window.__TAURI__.core.invoke('get_ptt_state');
        if (cancelled) return;
        const enabled = !!(s && s.enabled);
        const shortcut = String((s && s.shortcut) || DEFAULT_PTT_SHORTCUT).trim() || DEFAULT_PTT_SHORTCUT;
        setPttEnabled(enabled);
        setPttShortcut(shortcut);
        setPushToTalkEnabled(enabled);
        setPushToTalkKeyCode(shortcut);
        emitPushToTalkPrefsChanged({ enabled, keyCode: shortcut });
        const fromApp = {
          mic: (s && (s.mute_mic_shortcut || s.muteMicShortcut)) || null,
          speaker: (s && (s.mute_speaker_shortcut || s.muteSpeakerShortcut)) || null,
          both: (s && (s.mute_both_shortcut || s.muteBothShortcut)) || null,
        };
        for (const k of Object.keys(fromApp)) if (fromApp[k]) writeMuteHotkey(k, fromApp[k]);
        const all = readAllMuteHotkeys();
        setHotkeys(all);
        await pushMuteHotkeysToDesktop(all);
      } catch {
        setPttEnabled(true);
        setPttShortcut(DEFAULT_PTT_SHORTCUT);
        setPushToTalkEnabled(true);
        setPushToTalkKeyCode(DEFAULT_PTT_SHORTCUT);
        emitPushToTalkPrefsChanged({ enabled: true, keyCode: DEFAULT_PTT_SHORTCUT });
        await pushMuteHotkeysToDesktop(hotkeysRef.current);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inTauri]);

  const setPtt = useCallback(
    async (enabled) => {
      setPttEnabled(enabled);
      const updated = await invokeTauri('set_ptt_enabled', { enabled });
      const on = updated ? !!updated.enabled : enabled;
      const shortcut = String((updated && updated.shortcut) || pttShortcut || DEFAULT_PTT_SHORTCUT).trim() || DEFAULT_PTT_SHORTCUT;
      setPttEnabled(on);
      setPttShortcut(shortcut);
      setPushToTalkEnabled(on);
      setPushToTalkKeyCode(shortcut);
      emitPushToTalkPrefsChanged({ enabled: on, keyCode: shortcut });
    },
    [pttShortcut]
  );

  const setPttKey = useCallback(async (next) => {
    const updated = await invokeTauri('set_ptt_shortcut', { shortcut: next });
    const shortcut = String((updated && updated.shortcut) || next).trim() || next;
    setPttShortcut(shortcut);
    if (updated) {
      setPushToTalkKeyCode(shortcut);
      emitPushToTalkPrefsChanged({ enabled: !!updated.enabled, keyCode: shortcut });
    }
  }, []);

  // Key capture: the next real key (with its modifiers) becomes the shortcut; Escape cancels.
  useEffect(() => {
    if (!capturing) return undefined;
    const onKeyDown = async (e) => {
      try {
        e.__voxShortcutCaptureHandled = true;
        e.preventDefault();
        e.stopPropagation();
      } catch {}
      if (e.code === 'Escape' && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey) {
        setCapturing('');
        return;
      }
      const next = shortcutFromKeyEvent(e);
      if (!next) return; // only modifiers so far
      const kind = capturing;
      setCapturing('');
      if (kind === 'ptt') {
        await setPttKey(next);
        return;
      }
      const others = Object.keys(MUTE_HOTKEYS)
        .filter((k) => k !== kind)
        .map((k) => hotkeysRef.current[k]);
      const issue = describeShortcutProblem(next, others);
      setProblem({ kind, text: issue });
      if (issue) return;
      await applyHotkey(kind, next);
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [capturing, applyHotkey, setPttKey]);

  return {
    inTauri,
    hotkeys,
    capturing,
    startCapture: (kind) => {
      setProblem({ kind: '', text: '' });
      setCapturing((cur) => (cur === kind ? '' : kind));
    },
    problem,
    clearHotkey: (kind) => applyHotkey(kind, ''),
    resetHotkey: (kind) => applyHotkey(kind, null),
    resetAllHotkeys: async () => {
      for (const k of Object.keys(MUTE_HOTKEYS)) writeMuteHotkey(k, null);
      const all = readAllMuteHotkeys();
      setHotkeys(all);
      announceMuteHotkeys();
      await pushMuteHotkeysToDesktop(all);
    },
    pttEnabled,
    pttShortcut,
    setPtt,
    resetPttKey: () => setPttKey(DEFAULT_PTT_SHORTCUT),
  };
}
